import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';

import { Formik, FormikProps } from 'formik';
import { Location } from 'history';

// components
import BasicSection from '../../components/display/layout/basicSection';
import Page from '../../components/display/layout/pageBasic';
import { getCMSFiltersMappedValues } from '../../components/formulas/settings/AttributesFilters/helper';
import { getTagsInitialValues } from '../../components/formulas/settings/FormulaTags/helper';
import { CompactTabItem } from '../../components/nav/CompactTabMenu/CompactTab';
import CompactTabMenu from '../../components/nav/CompactTabMenu/CompactTabMenu';
import AampeOverlay from '../../components/shared/AampeOverlay';
import FormulaLanguageSelection from 'components/formula-language-selection/FormulaLanguageSelection';
// helpers
import styled from 'styled-components';

// theme
import aampeTheme from '../../constants/aampeTheme';
import { FormulaType } from '../../constants/formulaType';
// constants
import routes from '../../constants/routes';

// contexts
import {
    defaultEditorDetails,
    EditorContext,
    EditorContextModel,
    EditorDetailsModel,
} from '../../context/EditorContext';

import AlternateListPreview from '../../editors/PushNotificationEditor/Preview/AlternateListPreview';

import {
    getEditFormulaFormValues,
    getExtraCharactersOfPlaceholders,
    getFormulaSchedulingType,
    getMaxCharacterFromEditorDetails,
} from '../../helpers/formulaHelpers';

// hooks
import useFilterParam from '../../hooks/aampeTable/useFilterParam';
import useRoleFlags from '../../hooks/auth/useRoleFlags';
import useCMSPlaceholderQuery from '../../hooks/cms-placeholders/useCMSPlaceholderQuery';
import useFormulaTimeSlotsMutation from '../../hooks/formulas/useFormulaTimeSlotMutation';
import useFormulaUpdateBaseMutation from '../../hooks/formulas/useFormulaUpdateBaseMutation';
import useMessageGenerationQuery from '../../hooks/formulas/useMessageGenerationQuery';
import useAnalytics from '../../hooks/helper/useAnalytics';
import useMessageContentGetAndUpdate from '../../hooks/message-content/useMessageContentGetAndUpdate';
import useFormulaTimingSlots from '../../hooks/timing-slots/useTimingSlots';
import useApprovedFormulaQueryV1 from 'hooks/formulas/useApprovedFormulaQueryV1';
import useQueryParams from 'hooks/helper/useQueryParams';

import { ALL_USERS_AUDIENCE_ID } from '../../models/audience.model';
import {
    CMSDisplayNames,
    CMSFilterType,
    CMSType,
    DEFAULT_CMS_TYPE_OPTION,
} from '../../models/cms-placeholder.model';
// models
import {
    ApprovedFormulaState,
    FormulaPreviewCount,
    FormulaStatus,
    FormulaViewSetupFormValue,
    FormulaViewTabs,
    FormulaViewTabsData,
    MaxCharacterModel,
    ProcessedFormulaV1,
} from '../../models/formula.model';

import { LocationState } from '../../types/location-state';

import AuditTrailWrapper from '../../views/activity-logs/AuditTrailWrapper';
// this is important!
import FormulaAlternateExport from '../../views/formulaCreate/formulaAlternate/FormulaAlternateExport';

import FormulaAampiConnect from '../formulaCreate/FormulaAampiConnect';
import FormulaFinalCheck from '../formulaCreate/FormulaFinalCheck';
import FormulaInfoComponent from '../formulaCreate/FormulaInfoComponent';
import CoverMain from '../formulaCreate/messagePreview/cover/CoverMain';
import FormulaEditView from './FormulaEditView';
import FormulaEditorView from './FormulaEditorView';

type FormulaViewParams = {
    formulaId: string;
};

const FormulaViewMain: React.FC<React.PropsWithChildren<unknown>> = () => {
    const location = useLocation<LocationState>();
    const history = useHistory<Location>();
    const { hasDeleteAccess, hasCreateFormulaAccess } = useRoleFlags();
    const { formulaId } = useParams<FormulaViewParams>();
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false);
    const [reinitializeMessageContent, setReinitializeMessageContent] =
        useState<number>(0);
    const { data, isLoading, isError, error, isFetching } =
        useApprovedFormulaQueryV1(parseInt(formulaId));

    const params = useQueryParams();

    const hasWriteAccess = useMemo<boolean>(() => {
        return data?.has_write_access ?? false;
    }, [data]);

    const categoryStatus = location.pathname
        .replace(`${routes.formulasMain}/`, '')
        ?.split('/')?.[0];

    const { track } = useAnalytics();
    const updateFormula = useFormulaUpdateBaseMutation(parseInt(formulaId));
    const {
        mutate: updateMessageContent,
        isUpdating: isUpdatingMessageContent,
        data: messageContentData,
    } = useMessageContentGetAndUpdate(parseInt(formulaId));

    const messageContentIdRef = useRef<string | undefined>(
        messageContentData?.id,
    );

    const updateTimeSlots = useFormulaTimeSlotsMutation(parseInt(formulaId));
    const { data: timingSlots } = useFormulaTimingSlots();
    const [editorDetails, setEditorDetails] =
        useState<EditorDetailsModel>(defaultEditorDetails);
    const { formula } = editorDetails;

    const isUpdating = updateFormula.isPending || isUpdatingMessageContent;

    const activeTabKey = useMemo<FormulaViewTabs>(() => {
        return params.tab
            ? params.tab
            : formula?.active_state &&
              [FormulaStatus.Review, ApprovedFormulaState.Live].includes(
                  formula?.active_state,
              )
            ? FormulaViewTabsData[0].key
            : FormulaViewTabsData[1].key;
    }, [formula]);

    const [tab, setTab] = useFilterParam('', 'tab', activeTabKey);

    useEffect(() => {
        const state = location.state as any;

        if (state?.startEditing) {
            setIsEditing(true);

            const { pathname, search, hash } = location;
            /* eslint-disable @typescript-eslint/no-unused-vars */
            const { startEditing, ...rest } = state;

            history.replace({
                pathname,
                search,
                hash,
                state: rest,
            });
        }
    }, [location, history]);
    useEffect(() => {
        setTab(activeTabKey);
    }, [activeTabKey, setTab]);

    useEffect(() => {
        if (data && !isLoading)
            setEditorDetails((prev) => ({
                ...prev,
                formula: data,
                formulaType: data.message_type,
                isApproved: data.is_approved,
                isControlledFormula: !!data?.is_controlled_message,
            }));
    }, [data, isLoading]);

    useEffect(() => {
        track('formula_viewed', {
            formulaId,
        });
    }, [formulaId, track]);

    const messageGenQuery = useMessageGenerationQuery(
        +formulaId,
        FormulaPreviewCount.default,
    );
    useEffect(() => {
        setEditorDetails((prevState) => ({
            ...prevState,
            previews: messageGenQuery.data || [],
            isPreviewError: Boolean(messageGenQuery.error),
            previewError: messageGenQuery.error?.message || null,
            isGenerating: false,
        }));
    }, [messageGenQuery.data, messageGenQuery.error]);

    const cmsPlaceholder = useCMSPlaceholderQuery();

    const extraPlaceholderChar = useMemo<MaxCharacterModel>(
        () =>
            getExtraCharactersOfPlaceholders(
                editorDetails,
                cmsPlaceholder.data?.messageable,
            ),
        [editorDetails, cmsPlaceholder.data],
    );

    const maxCharacters = useMemo<MaxCharacterModel>(
        () =>
            getMaxCharacterFromEditorDetails(
                editorDetails,
                extraPlaceholderChar,
            ),
        [editorDetails, extraPlaceholderChar],
    );

    const scheduleType = useMemo(() => {
        if (data && timingSlots) {
            return getFormulaSchedulingType(timingSlots, data);
        }
    }, [data, timingSlots]);

    const initialValues = useMemo<FormulaViewSetupFormValue>(
        () => ({
            name: data?.name,
            ...getTagsInitialValues(data?.tags ?? []),
            compound_trigger: data?.compound_trigger
                ? {
                      label: data?.compound_trigger?.name,
                      value: data?.compound_trigger?.id,
                  }
                : null,
            audience_id:
                data?.audiences?.[0]?.audience_id ?? ALL_USERS_AUDIENCE_ID,
            cms_type: data?.cms_type
                ? {
                      label: CMSDisplayNames[data?.cms_type],
                      value: data?.cms_type,
                  }
                : DEFAULT_CMS_TYPE_OPTION,
            cms_filters: data?.cms_placeholder_filters ?? [],
            cms_filter_type: data?.cms_placeholder_filters?.length
                ? CMSFilterType.SPECIFIC
                : CMSFilterType.GENERAL,
            send_start: data?.send_start,
            send_start_date: data?.send_start_date,
            labels: data?.labels,
            send_stop: data?.send_stop,
            send_stop_date: data?.send_stop_date,
            time_slot_ids: data?.time_slot_ids,
            is_controlled_message: data?.is_controlled_message,
            timezone: data?.timezone,
            scheduling_type: scheduleType,
            topic_id: data?.topic_id,
            reward_function_id: data?.reward_function_id,
        }),

        [data, scheduleType],
    );

    useEffect(() => {
        if (isFormSubmitting && !isUpdating && !updateTimeSlots.isPending) {
            setIsEditing(false);
            setIsFormSubmitting(false);
        }
    }, [isFormSubmitting, isUpdating, updateTimeSlots]);

    useEffect(() => {
        if (messageContentIdRef.current !== messageContentData?.id) {
            setReinitializeMessageContent((prev) => prev + 1);
            messageContentIdRef.current = messageContentData?.id;
        }
    }, [messageContentData?.id]);

    const onFormSubmit = (values: FormulaViewSetupFormValue) => {
        let isFormLoading = false;
        const {
            tags,
            formulaName,
            compoundTriggerId,
            isTagsChanged,
            isAudienceChanged,
            updatedAudienceId,
            isCompoundTriggerChanged,
            isNameChanged,
            isCMSTypeChanged,
            cmsValue,
            isCmsFilterChanged,
            isStartTimeChanged,
            isLabelChanged,
            isStartDateChanged,
            isStopDateChanged,
            isStopTimeChanged,
            isTimeZoneChanged,
            isTimeSlotsIdsChange,
            isSchedulingTypeChange,
            isTopicIdChanged,
            isRewardFunctionIdChanged,
        } = getEditFormulaFormValues(initialValues, values);
        if (isTimeSlotsIdsChange) {
            isFormLoading = true;
            updateTimeSlots.mutate({
                time_slot_ids: values.time_slot_ids || [],
            });
        }

        if (
            isNameChanged ||
            isCMSTypeChanged ||
            isTagsChanged ||
            isAudienceChanged ||
            isCompoundTriggerChanged ||
            isCmsFilterChanged ||
            isStartTimeChanged ||
            isLabelChanged ||
            isStartDateChanged ||
            isStopDateChanged ||
            isStopTimeChanged ||
            isTimeZoneChanged ||
            isSchedulingTypeChange ||
            isTopicIdChanged ||
            isRewardFunctionIdChanged
        ) {
            isFormLoading = true;

            updateFormula.mutate({
                id: parseInt(formulaId),
                ...(isNameChanged ? { name: formulaName } : {}),
                ...(isTagsChanged ? { tags: tags } : {}),
                ...(isAudienceChanged
                    ? { audience_id: updatedAudienceId }
                    : {}),
                ...(isCompoundTriggerChanged
                    ? { compound_trigger_id: compoundTriggerId as string }
                    : {}),
                ...(isCmsFilterChanged
                    ? {
                          cms_placeholder_filters:
                              getCMSFiltersMappedValues(values),
                      }
                    : {}),
                ...(isStartTimeChanged
                    ? { send_start: values.send_start }
                    : {}),
                ...(isStartDateChanged
                    ? { send_start_date: values.send_start_date }
                    : {}),
                ...(isLabelChanged
                    ? {
                          labels: values.labels,
                      }
                    : {}),
                ...(isStopDateChanged
                    ? { send_stop_date: values.send_stop_date }
                    : {}),
                ...(isStopTimeChanged ? { send_stop: values.send_stop } : {}),
                ...(isTimeZoneChanged ? { timezone: values.timezone } : {}),
                ...(isTopicIdChanged ? { topic_id: values.topic_id } : {}),
                ...(isRewardFunctionIdChanged
                    ? { reward_function_id: values.reward_function_id }
                    : {}),
            });
            if (isCMSTypeChanged) {
                updateMessageContent({
                    id: messageContentIdRef.current!,
                    cms_type: cmsValue as CMSType,
                });
            }
        }
        if (isFormLoading) {
            setIsFormSubmitting(true);
        }
    };

    const formulaViewTabs = useMemo<CompactTabItem[]>(() => {
        const { formulaType, formula } = editorDetails;

        let tabs = FormulaViewTabsData;

        if (
            formula?.active_state &&
            ![FormulaStatus.Review, ApprovedFormulaState.Live].includes(
                formula.active_state,
            )
        ) {
            tabs = tabs.filter((tab) => tab.key !== FormulaViewTabs.COVER);
        }

        return formulaType === FormulaType.contentCards
            ? tabs
            : tabs.filter((tab) => tab.key !== FormulaViewTabs.AAMPE_CONNECT);
    }, [editorDetails]);

    if (data?.surface_id) {
        return (
            <Redirect
                to={{
                    pathname: routes.surfaceFormulaDetails(
                        data?.surface_id,
                        data?.id,
                    ),
                    state: history.location.state,
                }}
            />
        );
    }

    if (
        data?.active_state &&
        data?.active_state !== categoryStatus &&
        !isFetching
    ) {
        return (
            <Redirect
                to={{
                    pathname: routes.formulaView(data?.active_state, formulaId),
                    state: location.state,
                }}
            />
        );
    }

    return (
        <EditorContext.Provider
            value={{ editorDetails, setEditorDetails } as EditorContextModel}
        >
            <Page
                isLoading={isLoading}
                isError={isError || !data}
                error={(error as any)?.message}
                currentBreadcrumb={{ label: data?.name ? data?.name : '' }}
            >
                <Formik initialValues={initialValues} onSubmit={onFormSubmit}>
                    {(formikProps: FormikProps<FormulaViewSetupFormValue>) => (
                        <Container>
                            <FormulaInfoComponent
                                isUpdating={isUpdating}
                                setIsEditing={setIsEditing}
                                isEditing={isEditing}
                                formikProps={formikProps}
                                isFormSubmitting={isFormSubmitting}
                                hasWriteAccess={hasWriteAccess}
                                hasCreateFormulaAccess={hasCreateFormulaAccess}
                                hasDeleteAccess={hasDeleteAccess}
                            />
                            {!isEditing ? (
                                <ContainerNoGap>
                                    <CompactTabMenu
                                        activeTabKey={tab as string}
                                        onTabClick={
                                            setTab as (value: string) => void
                                        }
                                        tabs={formulaViewTabs}
                                    />
                                    {[
                                        FormulaViewTabs.CONTENT,
                                        FormulaViewTabs.ALTERNATES,
                                    ].includes(tab as FormulaViewTabs) && (
                                        <FormulaLanguageSelection
                                            allowLanguageSelection={false}
                                            hasWriteAccess={hasWriteAccess}
                                        />
                                    )}
                                    <BasicSection belowTabs>
                                        <TabContent>
                                            {tab === FormulaViewTabs.COVER && (
                                                <CoverMain
                                                    formulaId={Number(
                                                        formulaId,
                                                    )}
                                                />
                                            )}
                                            {tab ===
                                                FormulaViewTabs.SUMMARY && (
                                                <>
                                                    <Heading>
                                                        Message summary
                                                    </Heading>
                                                    <FormulaFinalCheck
                                                        isFormulaView={true}
                                                    />
                                                </>
                                            )}
                                            {tab ===
                                                FormulaViewTabs.ALTERNATES && (
                                                <>
                                                    <Content>
                                                        <Heading>
                                                            Alternates
                                                        </Heading>
                                                        {hasWriteAccess && (
                                                            <FormulaAlternateExport />
                                                        )}
                                                    </Content>

                                                    <AlternateListPreview
                                                        formulaStructure={
                                                            editorDetails
                                                                .formula
                                                                ?.structure ||
                                                            []
                                                        }
                                                        extraPlaceholderCharacters={
                                                            extraPlaceholderChar
                                                        }
                                                        formulaId={Number(
                                                            formulaId,
                                                        )}
                                                        isEditable={false}
                                                    />
                                                </>
                                            )}
                                            {tab ===
                                                FormulaViewTabs.CONTENT && (
                                                <>
                                                    <Heading>
                                                        Message content
                                                    </Heading>
                                                    <StyledFormulaEditorView
                                                        key={
                                                            reinitializeMessageContent
                                                        }
                                                        data={
                                                            data as ProcessedFormulaV1
                                                        }
                                                        maxCharacterCounts={
                                                            maxCharacters
                                                        }
                                                        isEditable={false}
                                                    />
                                                </>
                                            )}
                                            {tab ===
                                                FormulaViewTabs.AAMPE_CONNECT && (
                                                <>
                                                    <Heading>
                                                        Content card
                                                    </Heading>
                                                    <FormulaAampiConnect />
                                                </>
                                            )}
                                            {tab === FormulaViewTabs.TRAIL && (
                                                <AuditTrailWrapper
                                                    formulaId={formulaId}
                                                />
                                            )}
                                        </TabContent>
                                    </BasicSection>
                                </ContainerNoGap>
                            ) : (
                                <FormulaEditView
                                    formikProps={formikProps}
                                    setIsEditing={setIsEditing}
                                    isFormSubmitting={isFormSubmitting}
                                    data={data}
                                    hasWriteAccess={hasWriteAccess}
                                />
                            )}
                        </Container>
                    )}
                </Formik>
                {isFormSubmitting && <AampeOverlay />}
            </Page>
        </EditorContext.Provider>
    );
};
const Container = styled.div(() => ({
    display: 'flex',
    borderRadius: aampeTheme.borderRadius.default,
    gap: aampeTheme.spacing(3),
    flexDirection: 'column',
}));

const ContainerNoGap = styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
}));

const TabContent = styled.div({
    backgroundColor: aampeTheme.colors.white,
    padding: aampeTheme.spacing(3),
    display: 'flex',
    gap: aampeTheme.spacing(4),
    flexDirection: 'column',
    borderRadius: aampeTheme.borderRadius.default,
});

const Heading = styled.span({
    fontSize: '24px',
    fontWeight: 500,
    lineHeight: '28px',
    color: aampeTheme.colors.gray800,
});

const StyledFormulaEditorView = styled(FormulaEditorView)({
    padding: aampeTheme.spacing(0),
    width: '100%',
    margin: aampeTheme.spacing(0),
});

const Content = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
});

export default FormulaViewMain;
